import React, { useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { CheckCircle2, Lock, Mail, ShieldCheck } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@components/common/Button';
import { LoadingSpinner } from '@components/common/LoadingSpinner';
import { useInviteAccept } from '@api/hooks/useInviteAccept';
import { useInviteValidation } from '@api/hooks/useInviteValidation';
import { brand } from '@/config/brand';
import { formatDateTime } from '@utils/formatters';

const passwordInputClass = (hasError: boolean) => (
  [
    'w-full pl-10 pr-3 py-2.5 rounded-lg text-sm transition-colors',
    'bg-[#0a1628] text-slate-100 placeholder:text-slate-500',
    'focus:outline-none focus:ring-2 focus:ring-offset-0',
    hasError
      ? 'border border-red-500/60 focus:ring-red-500/20'
      : 'border border-[#1e3a5f] focus:border-primary-500 focus:ring-primary-500/20',
  ].join(' ')
);

const getInviteErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 404) {
      return 'This invite link was not found.';
    }
    if (error.response?.status === 410) {
      return 'This invite link has expired or was already used.';
    }
  }
  return 'We could not verify this invite. Please ask your EZ AP contact for a fresh link.';
};

const getAcceptErrorMessage = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response?.status === 422) {
      return 'Password must be between 8 and 128 characters.';
    }
    if (error.response?.status === 410) {
      return 'This invite link has expired or was already used.';
    }
  }
  return 'Could not complete setup. Please try again.';
};

export const InviteAcceptPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const validation = useInviteValidation(token);
  const acceptInvite = useInviteAccept();
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const mismatch = confirmPassword.length > 0 && password !== confirmPassword;
  const tooShort = password.length > 0 && password.length < 8;
  const canSubmit = Boolean(
    token &&
    validation.isSuccess &&
    password.length >= 8 &&
    password.length <= 128 &&
    password === confirmPassword &&
    !acceptInvite.isPending,
  );
  const expiresAt = useMemo(() => {
    if (!validation.data?.expires_at) {
      return null;
    }
    return formatDateTime(validation.data.expires_at);
  }, [validation.data?.expires_at]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSubmit || !token) {
      return;
    }

    const result = await acceptInvite.mutateAsync({ token, password });
    navigate(result.redirect_to, { replace: true });
  };

  const pageFrame = (children: React.ReactNode) => (
    <div className="min-h-screen flex items-center justify-center bg-[#060e1f] p-4">
      <div className="pointer-events-none fixed inset-0 flex items-center justify-center">
        <div className="h-[600px] w-[600px] rounded-full bg-teal-500/5 blur-3xl" />
      </div>
      <div className="relative z-10 w-full max-w-md">{children}</div>
    </div>
  );

  if (!token) {
    return pageFrame(
      <div className="bg-[#0d1f3c] border border-[#1e3a5f] rounded-xl shadow-xl p-8 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-red-300" />
        <h1 className="mt-4 text-xl font-bold text-slate-100">Invite link missing</h1>
        <p className="mt-2 text-sm text-slate-400">
          Open the setup link from your invite email to continue.
        </p>
      </div>,
    );
  }

  if (validation.isPending) {
    return pageFrame(<LoadingSpinner text="Checking invite..." />);
  }

  if (validation.isError) {
    return pageFrame(
      <div className="bg-[#0d1f3c] border border-[#1e3a5f] rounded-xl shadow-xl p-8 text-center">
        <ShieldCheck className="mx-auto h-10 w-10 text-red-300" />
        <h1 className="mt-4 text-xl font-bold text-slate-100">Invite unavailable</h1>
        <p className="mt-2 text-sm text-slate-400">
          {getInviteErrorMessage(validation.error)}
        </p>
      </div>,
    );
  }

  return pageFrame(
    <div className="space-y-8">
      <div className="flex flex-col items-center">
        <img src={brand.logoUrl} alt={brand.name} className="h-12 w-12 mb-3" />
        <span className="text-lg font-semibold text-slate-200 tracking-tight">
          {brand.name}
        </span>
      </div>

      <div className="bg-[#0d1f3c] border border-[#1e3a5f] rounded-xl shadow-xl p-8 space-y-6">
        <div className="space-y-3 text-center">
          <CheckCircle2 className="mx-auto h-10 w-10 text-teal-300" />
          <div>
            <h1 className="text-xl font-bold text-slate-100">Set up your admin account</h1>
            <p className="mt-1 text-sm text-slate-400">
              You have been invited to manage {validation.data.tenant_name}.
            </p>
          </div>
        </div>

        <div className="rounded-lg border border-[#1e3a5f] bg-[#0a1628] px-4 py-3 text-sm text-slate-300">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-slate-500" />
            <span className="truncate">{validation.data.email}</span>
          </div>
          {expiresAt && (
            <p className="mt-2 text-xs text-slate-500">Invite expires {expiresAt}</p>
          )}
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-1.5">
            <label htmlFor="password" className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <input
                id="password"
                type="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                placeholder="At least 8 characters"
                className={passwordInputClass(tooShort)}
                disabled={acceptInvite.isPending}
                autoComplete="new-password"
              />
            </div>
            {tooShort && (
              <p className="text-xs text-red-400">Password must be at least 8 characters.</p>
            )}
          </div>

          <div className="space-y-1.5">
            <label htmlFor="confirm-password" className="block text-xs font-medium text-slate-400 uppercase tracking-wider">
              Confirm password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-2.5 h-4 w-4 text-slate-500" />
              <input
                id="confirm-password"
                type="password"
                value={confirmPassword}
                onChange={(event) => setConfirmPassword(event.target.value)}
                placeholder="Repeat password"
                className={passwordInputClass(mismatch)}
                disabled={acceptInvite.isPending}
                autoComplete="new-password"
              />
            </div>
            {mismatch && (
              <p className="text-xs text-red-400">Passwords do not match.</p>
            )}
          </div>

          {acceptInvite.isError && (
            <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm text-red-200">
              {getAcceptErrorMessage(acceptInvite.error)}
            </p>
          )}

          <Button
            type="submit"
            disabled={!canSubmit}
            loading={acceptInvite.isPending}
            className="w-full flex items-center justify-center gap-2"
          >
            Continue
          </Button>
        </form>
      </div>
    </div>,
  );
};

export default InviteAcceptPage;
